/**
 * Reactive state for the On-Screen Debugger "Video" tab.
 * Players (Bitmovin, etc.) set manifest + playback info; debugger reads it and shows device capabilities.
 */
import { ref, shallowRef } from 'vue';
import type { StreamMetadataLabel } from '@/utils/stream-label-detector';

export interface PlaybackDebugInfo {
  /** Current video bitrate (bps) */
  bitrate?: number;
  /** Video codec string (e.g. avc1.640028, dvh1.08.07) */
  videoCodec?: string;
  /** Audio codec string */
  audioCodec?: string;
  /** Current width */
  width?: number;
  /** Current height */
  height?: number;
  /** Whether current rendition is Dolby Vision */
  dolbyVision?: boolean;
  /** HDR type of current rendition (e.g. HDR10, HLG) */
  hdrType?: string;
  /** Protocol (hls/dash) */
  protocol?: string;
  /** Quality label if available (e.g. 720p, 1080p) */
  qualityLabel?: string;
}

export interface DeviceCapabilitiesDebug {
  /** UHD/4K panel supported */
  uhd?: boolean;
  /** Generic HDR supported */
  hdr?: boolean;
  /** HDR types (e.g. HDR10, HLG) */
  hdrTypes?: string[];
  /** Dolby Vision supported */
  dolbyVision?: boolean;
  /** Raw device info line for display */
  summary?: string;
}

/** Single video rendition from the manifest (available quality level). */
export interface ManifestRendition {
  height?: number;
  width?: number;
  bitrate?: number;
  id?: string;
}

/** Single audio track from the manifest. */
export interface ManifestAudioTrack {
  id?: string;
  label?: string;
  lang?: string;
}

const manifestUrl = ref('');
const manifestProtocol = ref<'hls' | 'dash' | ''>('');
const manifestLabels = ref<StreamMetadataLabel[]>([]);
const availableRenditions = ref<ManifestRendition[]>([]);
const availableAudioTracks = ref<ManifestAudioTrack[]>([]);
const playbackInfo = shallowRef<PlaybackDebugInfo | null>(null);
const deviceCapabilities = shallowRef<DeviceCapabilitiesDebug | null>(null);

export function useVideoDebugStore() {
  return {
    manifestUrl,
    manifestProtocol,
    manifestLabels,
    availableRenditions,
    availableAudioTracks,
    playbackInfo,
    deviceCapabilities,
    setManifest(url: string, protocol?: 'hls' | 'dash') {
      manifestUrl.value = url || '';
      manifestProtocol.value =
        protocol || (url?.toLowerCase().includes('.mpd') ? 'dash' : url?.toLowerCase().includes('.m3u8') ? 'hls' : '');
    },
    setManifestLabels(labels: StreamMetadataLabel[]) {
      manifestLabels.value = labels || [];
    },
    setAvailableRenditions(renditions: ManifestRendition[]) {
      availableRenditions.value = renditions || [];
    },
    setAvailableAudioTracks(tracks: ManifestAudioTrack[]) {
      availableAudioTracks.value = tracks || [];
    },
    setPlaybackInfo(info: PlaybackDebugInfo | null) {
      playbackInfo.value = info;
    },
    setDeviceCapabilities(caps: DeviceCapabilitiesDebug | null) {
      deviceCapabilities.value = caps;
    },
  };
}
